import { DISCLAIMER_RELEVANCE_YEARS, TIME_PERIODS, TIME_RANGE_PRESET } from './constants';
import moment from 'moment';
import { userHasPermissionEnabled, PERMISSIONS } from './permissions';
import { periodToMonths } from './time';
import { Proposal } from '@/modules/proposal/Proposal';

// Types
import Nullable from '@/types/nullable';

export const getDateQuickRangeFromPreset = (preset: TIME_RANGE_PRESET, useLongHistory?: boolean, displaycommonInception?: boolean) => {
  const defaultPeriods = [
    TIME_PERIODS.YT,
    TIME_PERIODS['1Y'],
    TIME_PERIODS['3Y'],
    TIME_PERIODS['5Y'],
    TIME_PERIODS['10Y'],
    useLongHistory ? TIME_PERIODS.INC : TIME_PERIODS.CI,
  ];

  switch (preset) {
    case TIME_RANGE_PRESET.MULTI_ITEMS_DEFAULT: {
      return defaultPeriods;
    }
    case TIME_RANGE_PRESET.SINGLE_ITEM_DEFAULT: {
      return [TIME_PERIODS.YT, TIME_PERIODS['1Y'], TIME_PERIODS['3Y'], TIME_PERIODS['5Y'], TIME_PERIODS['10Y'], TIME_PERIODS.INC];
    }
    case TIME_RANGE_PRESET.RISK_DEFAULT: {
      const riskTimePeriods = [TIME_PERIODS.YT, TIME_PERIODS['1Y'], TIME_PERIODS['3Y'], TIME_PERIODS['5Y'], TIME_PERIODS['10Y'], TIME_PERIODS.INC];
      if (displaycommonInception) {
        riskTimePeriods.push(TIME_PERIODS.CI);
      }
      return riskTimePeriods;
    }
    case TIME_RANGE_PRESET.CALENDAR_YEAR_DEFAULT: {
      return [TIME_PERIODS['5Y'], TIME_PERIODS['10Y']];
    }
    case TIME_RANGE_PRESET.RISK_REWARD_DEFAULT: {
      return [TIME_PERIODS.YT, TIME_PERIODS['1Y'], TIME_PERIODS['3Y'], TIME_PERIODS['5Y'], TIME_PERIODS['10Y'], TIME_PERIODS.CI];
    }
    case TIME_RANGE_PRESET.PORTFOLIO_CORRELATION_MATRIX: {
      const portfolioCorrelationPeriods = [TIME_PERIODS.YT, TIME_PERIODS['1Y'], TIME_PERIODS['3Y'], TIME_PERIODS['5Y'], TIME_PERIODS['10Y'], TIME_PERIODS.INC];
      if (displaycommonInception) {
        portfolioCorrelationPeriods.push(TIME_PERIODS.CI);
      }
      return portfolioCorrelationPeriods;
    }
    case TIME_RANGE_PRESET.INCOME_STREAM: {
      return [TIME_PERIODS.YT, TIME_PERIODS['1Y'], TIME_PERIODS['3Y'], TIME_PERIODS['5Y'], TIME_PERIODS['10Y']];
    }
  }

  return defaultPeriods;
};

/**
 * Utility function to get date quick range values from toolbar configuration
 * This consolidates the logic used in both ProposalSectionComponent and ProposalToolbar
 */
export const getDateQuickRangeFromToolbarConfig = (
  toolbarConfig?: Nullable<{
    dateQuickRangeValues?: TIME_PERIODS[];
    dateQuickRangePreset?: TIME_RANGE_PRESET;
  }>,
  proposal?: Pick<Proposal, 'appUser' | 'items' | 'shortHistory'>,
): TIME_PERIODS[] => {
  if (toolbarConfig?.dateQuickRangeValues) {
    return toolbarConfig.dateQuickRangeValues;
  }

  if (toolbarConfig?.dateQuickRangePreset && proposal) {
    const displaycommonInception = userHasPermissionEnabled(proposal.appUser, PERMISSIONS.COMMON_INCEPTION) && proposal.items.length > 1;
    return getDateQuickRangeFromPreset(toolbarConfig.dateQuickRangePreset, proposal.shortHistory, displaycommonInception);
  }

  return getDateQuickRangeFromPreset(TIME_RANGE_PRESET.SINGLE_ITEM_DEFAULT);
};

/**
 * Core utility function for calculating date ranges from time periods
 * This consolidates the common logic used across different date range calculations
 * @param dateQuickRange The time period to convert
 * @param options Configuration options for calculation
 * @returns Object with calculated date range and metadata
 */
export const calculateDateRangeFromPeriod = (
  dateQuickRange: TIME_PERIODS,
  options: {
    referenceEndDate?: Date;
    items?: any[]; // ComparisonItem[] but avoiding circular import
    useMonthly?: boolean;
    getEarliestCommonInceptionDate?: (items: any[], useMonthly: boolean) => Date | null;
    applyRelevanceYearLimit?: boolean;
  } = {},
): {
  startDate: Date | null;
  endDate: Date;
  isYTD?: boolean;
  months?: number | null;
  isInception?: boolean;
} => {
  const { referenceEndDate = moment().toDate(), items = [], useMonthly = false, getEarliestCommonInceptionDate, applyRelevanceYearLimit = false } = options;

  let startDate: Date | null = null;
  let months: number | null = null;
  let isInception = false;

  if (dateQuickRange === TIME_PERIODS.CI || dateQuickRange === TIME_PERIODS.INC) {
    isInception = true;

    if (items.length > 0 && getEarliestCommonInceptionDate) {
      const earliestCommonDate = getEarliestCommonInceptionDate(items, useMonthly);
      if (earliestCommonDate) {
        startDate = earliestCommonDate;
      }
    }
  } else if (dateQuickRange === TIME_PERIODS.YT) {
    startDate = moment(referenceEndDate).startOf('year').toDate();
    months = moment(referenceEndDate).diff(startDate, 'months');
  } else if (dateQuickRange === TIME_PERIODS.MT) {
    startDate = moment(referenceEndDate).startOf('month').toDate();
    months = 1;
  } else {
    months = periodToMonths(dateQuickRange);
    if (months && months > 0) {
      startDate = moment(referenceEndDate).subtract(months, 'months').startOf('month').toDate();
    }
  }

  if (applyRelevanceYearLimit && !startDate) {
    startDate = moment(referenceEndDate).subtract(DISCLAIMER_RELEVANCE_YEARS, 'years').toDate();
  }

  return {
    startDate,
    endDate: referenceEndDate,
    isYTD: dateQuickRange === TIME_PERIODS.YT || (months != null && months > 0 && months < 12),
    months,
    isInception,
  };
};
